// @ts-nocheck
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { ExtensionOff } from '@mui/icons-material';
import { EMPTY_ARRAY } from '../const/CommonConst';
import NoDataPresentComponent from '../components/Common/NoDataPresentComponent';

const PluginDisabledIcon = () => (
  <ExtensionOff sx={{ fontSize: 64, color: 'text.secondary', mb: 1 }} />
);

const DefaultFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    height="calc(100vh - 200px)"
  >
    <NoDataPresentComponent
      message="Plugin not enabled for your organisation"
      Icon={PluginDisabledIcon}
    />
  </Box>
);

/**
 * PluginComponent - Dynamically renders a component from the plugin configuration
 *
 * This component looks up a plugin component from the tenant's plugin configuration
 * using the provided pluginKey. If the component is found, it renders the plugin component
 * with all passed props. If not found, it renders the children as a fallback.
 *
 * @param {Object} props - Component props
 * @param {import('react').ReactNode} props.children - Fallback content to render if plugin component is not found
 * @param {string} props.pluginKey - The key to look up the component in pluginConfig
 * @param {...any} props - Any additional props to pass to the plugin component
 */
const PluginAccessView = ({
  children,
  pluginKey,
  permissions = EMPTY_ARRAY,
  ...props
}) => {
  // Get the plugin configuration from the tenant state
  const { pluginConfig } = useSelector((state) => state.tenant);
  const { permissions: userPermissions } = useSelector((state) => state.users);

  // Look up the component using the provided plugin key
  const Component = get(pluginConfig, pluginKey);

  const hasAccess = useMemo(() => {
    if (!permissions || permissions?.length === 0) {
      return true;
    }

    return Object.keys(userPermissions)
      ?.filter((permissionKey) => permissions?.includes(permissionKey))
      .some((permissionKey) =>
        ['View', 'Manage'].includes(userPermissions?.[permissionKey])
      );
  }, [userPermissions, permissions]);

  // If the plugin component exists, render it with the provided props
  if (Component && hasAccess) {
    return <Component {...props} />;
  }

  // Otherwise, render the fallback children or default fallback
  return children || <DefaultFallback />;
};

PluginAccessView.propTypes = {
  children: PropTypes.node,
  pluginKey: PropTypes.string.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string),
};

export default PluginAccessView;
